// ----------------------------------------------------------------------------
// Component: Tab
// ----------------------------------------------------------------------------
//
// TAB component with desktop tab functionality.
//
// Requires the following structure:
// .js-tab__item
//     .js-tab__button
//     .js-tab__content
//
// When the button is clicked, 'isOpen' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import Swiper from 'swiper';
import { Navigation, A11y, Keyboard } from 'swiper/modules';
import 'snippets/_containers/css-grid/css-grid';
import 'snippets/icon/icon';

// Styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import './_tabs.scss';

// -------------------------------------
// Variables
// -------------------------------------

var TAB_SECTION = '.section--tabs';
var TAB_ACCORDION = '.js-tabs--accordion';
var TAB_ITEM_ACCORDION = '.js-tab__item--accordion';
var TAB_TITLE_ACCORDION = '.js-tab__title--accordion';
var TAB_BUTTON_ACCORDION = '.js-tab__button--accordion';
var TAB_CONTENT_ACCORDION = '.js-tab__content--accordion';
var TAB_HORIZONTAL = '.js-tabs--horizontal';
var TAB_BUTTON_HORIZONTAL = '.js-tab__button--horizontal';
var TAB_CONTENT_HORIZONTAL = '.js-tab__content--horizontal';
var OPEN_CLASS = 'is-open';
var OPEN_CLASS_SELECTOR = '.is-open';
var SWIPER_CLASS = 'swiper--tabs';
var SWIPER_CLASS_SELECTOR = '.swiper--tabs';
var SWIPER_NAV_PREV_CLASS = 'swiper-button-prev--tabs';
var SWIPER_NAV_PREV_CLASS_SELECTOR = '.swiper-button-prev--tabs';
var SWIPER_NAV_NEXT_CLASS = 'swiper-button-next--tabs';
var SWIPER_NAV_NEXT_CLASS_SELECTOR = '.swiper-button-next--tabs';
var HORIZONTAL_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
var ACCORDION_KEYS = ['ArrowUp', 'ArrowDown', 'Home', 'End'];

// -------------------------------------
// Helper functions
// -------------------------------------

// Initialize horizontal tabs
function initHorizontalTabs(buttons, content) {
  buttons.forEach(function (button) {
    button.addEventListener('click', function (event) {
      event.preventDefault();
      updateHorizontalTabs(event.target, buttons, content);
    });
    button.addEventListener('keydown', function (event) {
      handleKeyboard('horizontal', event, buttons, content);
    });
  });
}

// Update the active horizontal tab
function updateHorizontalTabs(button, allButtons, allContent) {
  var content = document.getElementById(button.getAttribute('aria-controls'));
  if (!button.classList.contains(OPEN_CLASS)) {
    // Close all content
    updateAttributeForElements(allButtons, 'aria-selected', 'false');
    updateAttributeForElements(allButtons, 'tabindex', '-1');
    removeClassFromElements(allButtons, OPEN_CLASS);
    removeClassFromElements(allContent, OPEN_CLASS);

    // Open current content
    openHorizontalTab(button, content);
  }
}

// Open a horizontal tab
function openHorizontalTab(button, content) {
  button.classList.add(OPEN_CLASS);
  button.setAttribute('aria-selected', 'true');
  button.setAttribute('tabindex', '0');
  content.classList.add(OPEN_CLASS);
}

// Initialize accordion tabs
function initAccordionTabs(buttons) {
  buttons.forEach(function (button) {
    button.addEventListener('click', function (event) {
      event.preventDefault();
      updateAccordionTabs(event.target);
    });
    button.addEventListener('keydown', function (event) {
      handleKeyboard('accordion', event, buttons, null);
    });
  });
}

// Update the active accordion tab
function updateAccordionTabs(button) {
  var tab = generateTab(button);
  var tabs = tab.$item.parentNode;
  var openClassElems = tabs.querySelectorAll(OPEN_CLASS_SELECTOR);
  var accordionContentItems = tabs.querySelectorAll(TAB_CONTENT_ACCORDION);
  var accordionButtons = tabs.querySelectorAll(TAB_BUTTON_ACCORDION);

  // Close all open tabs
  removeClassFromElements(openClassElems, OPEN_CLASS);
  updateStyleForElements(accordionContentItems, 'overflow', 'hidden');
  updateAttributeForElements(accordionButtons, 'aria-expanded', 'false');

  // Set tabindex for all content links
  accordionContentItems.forEach(function (contentItem) {
    var itemLinks = contentItem.querySelectorAll('a');
    updateAttributeForElements(itemLinks, 'tabindex', '-1');
  });

  // If the curent tab isn't already open, open it
  if (tab.$isOpening) {
    openAccordionTab(tab);
  }
}

// Open an accordion tab
function openAccordionTab(tab) {
  tab.$item.classList.add(OPEN_CLASS);
  tab.$button.classList.add(OPEN_CLASS);
  tab.$content.classList.add(OPEN_CLASS);
  tab.$button.setAttribute('aria-expanded', 'true');
  tab.$content.setAttribute('hidden', false);
  var contentItemLinks = tab.$content.querySelectorAll('a');
  updateAttributeForElements(contentItemLinks, 'tabindex', null);

  // This allows select boxes to flow out after it's opened
  setTimeout(function () {
    tab.$content.style.overflow = 'visible';
  }, 200);
}

// Builds a "tab" object
function generateTab(button) {
  var tab = {
    $container: button.closest(TAB_ACCORDION),
    $item: button.closest(TAB_ITEM_ACCORDION),
    $title: button.closest(TAB_TITLE_ACCORDION),
    $button: button,
    $content: button.closest(TAB_ITEM_ACCORDION).querySelector(TAB_CONTENT_ACCORDION),
    $isOpening: !button.classList.contains(OPEN_CLASS)
  };
  return tab;
}

// Handle keyboard events for buttons
function handleKeyboard(source, event, buttons, content) {
  var $this = event.target;
  var key = event.key;
  var sourceKeys = source === 'horizontal' ? HORIZONTAL_KEYS : ACCORDION_KEYS;
  if (sourceKeys.includes(key)) {
    event.preventDefault();
    var index;
    var length = buttons.length;
    var newIndex;
    for (var i = 0; i < length; i++) {
      if (buttons[i] === $this) {
        index = i;
        break;
      }
    }
    switch (key) {
      case 'ArrowUp':
        newIndex = (index + length - 1) % length;
        buttons[newIndex].focus();
        break;
      case 'ArrowDown':
        newIndex = (index + length + 1) % length;
        buttons[newIndex].focus();
        break;
      case 'ArrowLeft':
        newIndex = (index + length - 1) % length;
        buttons[newIndex].focus();
        updateHorizontalTabs(buttons[newIndex], buttons, content);
        break;
      case 'ArrowRight':
        newIndex = (index + length + 1) % length;
        buttons[newIndex].focus();
        updateHorizontalTabs(buttons[newIndex], buttons, content);
        break;
      case 'Home':
        buttons[0].focus();
        if (source === 'horizontal') {
          updateHorizontalTabs(buttons[0], buttons, content);
        }
        break;
      case 'End':
        newIndex = (length - 1) % length;
        buttons[newIndex].focus();
        if (source === 'horizontal') {
          updateHorizontalTabs(buttons[newIndex], buttons, content);
        }
        break;
    }
  }
}

// Update a given attribute for a list of elements
function updateAttributeForElements(elems, attribute, value) {
  elems.forEach(function (elem) {
    elem.setAttribute(attribute, value);
  });
}

// Remove a given class from a list of elements
function removeClassFromElements(elems, className) {
  elems.forEach(function (elem) {
    elem.classList.remove(className);
  });
}

// Update a given style for a list of elements
function updateStyleForElements(elems, style, value) {
  elems.forEach(function (elem) {
    elem.style[style] = value;
  });
}

// -------------------------------------
// Init
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  var tabSections = document.querySelectorAll(TAB_SECTION);
  tabSections.forEach(function (tabSection, index) {
    var horizontalTabs = tabSection.querySelector(TAB_HORIZONTAL);
    var horizontalTabButtons = horizontalTabs.querySelectorAll(TAB_BUTTON_HORIZONTAL);
    var horizontalTabContent = tabSection.querySelectorAll(TAB_CONTENT_HORIZONTAL);
    var accordionTabs = tabSection.querySelector(TAB_ACCORDION);

    // Set tabindex for all horizontal tab buttons
    updateAttributeForElements(horizontalTabButtons, 'tabindex', '-1');

    // Open the first horizontal tab by default
    openHorizontalTab(horizontalTabButtons[0], horizontalTabContent[0]);

    // Initialize horizontal tab buttons
    initHorizontalTabs(horizontalTabButtons, horizontalTabContent);

    // If there's an accordion, initialize it
    // Otherwise, initialize swiper for horizontal tabs
    if (accordionTabs !== null) {
      var accordionTabContentItemLinks = tabSection.querySelectorAll(TAB_CONTENT_ACCORDION + ' a');
      var accordionTabButtons = tabSection.querySelectorAll(TAB_BUTTON_ACCORDION);
      var tab = generateTab(accordionTabButtons[0]);

      // Set tabindex for all content links
      updateAttributeForElements(accordionTabContentItemLinks, 'tabindex', '-1');

      // Open the first tab by default
      openAccordionTab(tab);

      // Initialize accordion tabs
      initAccordionTabs(accordionTabButtons);
    } else {
      var swiperContainer = tabSection.querySelector(SWIPER_CLASS_SELECTOR);
      var swiperPrevButton = tabSection.querySelector(SWIPER_NAV_PREV_CLASS_SELECTOR);
      var swiperNextButton = tabSection.querySelector(SWIPER_NAV_NEXT_CLASS_SELECTOR);
      var mobileBlockCount = swiperContainer.dataset.blockCount > 2 ? 2.1 : swiperContainer.dataset.blockCount;
      var blockCount = swiperContainer.dataset.blockCount;
      swiperContainer.classList.add(SWIPER_CLASS + '-' + index);
      swiperPrevButton.classList.add(SWIPER_NAV_PREV_CLASS + '-' + index);
      swiperNextButton.classList.add(SWIPER_NAV_NEXT_CLASS + '-' + index);
      new Swiper(SWIPER_CLASS_SELECTOR + '-' + index, {
        modules: [Navigation, A11y, Keyboard],
        slidesPerView: mobileBlockCount,
        spaceBetween: 0,
        autoHeight: true,
        observer: true,
        observeParents: true,
        a11y: true,
        watchOverflow: true,
        navigation: {
          prevEl: SWIPER_NAV_PREV_CLASS_SELECTOR + '-' + index,
          nextEl: SWIPER_NAV_NEXT_CLASS_SELECTOR + '-' + index
        },
        breakpoints: {
          768: {
            slidesPerView: blockCount
          }
        }
      });
    }
  });
});